import {
  mapBackendTemplateToFrontend,
  ThumbnailPreview,
} from "components/CommonComponents/CertificatePreview";
import AdminHeader from "components/Headers/AdminHeader";
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Progress,
  Container,
  Row,
  Col,
} from "reactstrap";
import {
  StaticCanvas,
  Image as FabricImage,
  Line,
  Textbox,
} from "fabric";
import { saveTraineeCertificateImage } from "action/certificate";
import { useDispatch, useSelector } from "react-redux";
import { traineesCertificate } from "constants/certificate";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";

function CertificateGenerationProgress() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notify } = useContext(NotificationContext);

  const location = useLocation();
  const batchDetails = location.state?.batchDetails;
  const encodedId = urlEncodedId(batchDetails?.id);

  const processingRef = useRef(false);
  const certificateData = useSelector((state) => state.certificate?.traineesCertificate);

  const [generationCompleted, setGenerationCompleted] = useState(false);
  const [generationFailed, setGenerationFailed] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!generationCompleted && !generationFailed) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [generationCompleted, generationFailed]);

  useEffect(() => {
    const userCertificates = certificateData?.userCertificates;

    if (
      !certificateData || !Array.isArray(userCertificates) || userCertificates.length === 0) {
      showAlert({
        title: "Certificate Data Not Found",
        message:
          "Certificate data is not available. Please try again from the Certificate Issuance page.",
        type: "warning",
        showCancel: false,
        confirmStyle: "deep-blue",
        confirmText: "OK",
        onConfirm: (closeAlert) => {
          closeAlert();
          navigate(role === "SUPER_ADMIN"
            ? "/super-admin/certificate/issuance"
            : "/admin/certificate/issuance"
          );
        },
      });

      return;
    }
  }, [certificateData, navigate]);

  const certificates = useMemo(() => {
    const userCertificates = certificateData?.userCertificates;

    if (
      !Array.isArray(userCertificates) || userCertificates.length === 0) {
      return [];
    }

    return userCertificates
      .map((certificate) => {
        if (!certificate?.certificateJSON?.trim()) {
          return null;
        }

        try {
          const certificateJson = JSON.parse(certificate.certificateJSON);

          const fakeItem = {
            id: certificate.id,
            name: certificate.traineeName || "",
            type: certificateJson?.TemplateType || "COMPLETION",
            bg_path: certificateJson?.PreviewImage || "",
            template: certificateJson,
          };

          const parsedTemplate = mapBackendTemplateToFrontend(fakeItem);

          if (!parsedTemplate) {
            return null;
          }

          return { ...certificate, parsedTemplate, };
        } catch (err) {
          return null;
        }
      })
      .filter(Boolean);
  }, [certificateData]);

  const totalCertificates = certificates.length;

  const completedCount =
    certificates.filter(
      (certificate) =>
        certificate.certificateGenerated === true
    ).length;

  const pendingCount = totalCertificates - completedCount;

  const currentCertificate =
    certificates.find(
      (certificate) =>
        certificate.certificateGenerated !== true
    );

  const progressPercentage =
    totalCertificates > 0
      ? Math.round(
        (completedCount /
          totalCertificates) *
        100
      )
      : 0;

  useEffect(() => {
    if (
      totalCertificates > 0 && pendingCount === 0) {
      setGenerationCompleted(true);
      processingRef.current = false;
    }
  }, [
    totalCertificates,
    pendingCount,
  ]);

  useEffect(() => {
    if (
      generationCompleted ||
      generationFailed ||
      !currentCertificate ||
      processingRef.current
    ) {
      return;
    }

    const generateCurrentCertificate = async () => {
      processingRef.current = true;
      setError("");

      try {
        const certificateImage = await generateCertificatePDF(currentCertificate);

        if (!certificateImage) {
          notify("warning", "Certificate file could not be generated.");
        }

        const response = await generateCertificateAPI(currentCertificate, certificateImage);

        if (response?.code !== 200) {
          notify("warning", response?.message || "Unable to generate certificate.");
        }

        const updatedUserCertificates =
          certificateData.userCertificates.map((certificate) =>
            certificate.id === currentCertificate.id
              ? {
                ...certificate,
                certificateGenerated: true,
              }
              : certificate
          );

        const updatedTraineesCertificate = {
          ...certificateData,
          userCertificates: updatedUserCertificates,
        };

        dispatch({
          type: traineesCertificate,
          payload: updatedTraineesCertificate,
        });

        processingRef.current = false;

      } catch (err) {
        processingRef.current = false;

        setError(
          err?.message ||
          "Unable to generate certificate."
        );

        setGenerationFailed(true);
      }
    };

    generateCurrentCertificate();

  }, [
    currentCertificate?.id,
    generationCompleted,
    generationFailed,
    dispatch,
  ]);

  const generateCertificatePDF =
    async (rowData) => {
      const parsedTemplate =
        rowData?.parsedTemplate;

      if (!parsedTemplate) {
        notify("warning", "Certificate template not found.");
      }

      const {
        width,
        height,
      } =
        parsedTemplate.canvasSize || {
          width:
            parsedTemplate.CanvasSize
              ?.Width || 707,

          height:
            parsedTemplate.CanvasSize
              ?.Height || 500,
        };

      const hiddenCanvasElement =
        document.createElement("canvas");

      hiddenCanvasElement.width = width;
      hiddenCanvasElement.height = height;

      const canvas =
        new StaticCanvas(
          hiddenCanvasElement,
          {
            width,
            height,
            selection: false,
          }
        );

      try {
        const getCorsUrl = (url) => {
          if (
            !url ||
            url.startsWith("data:")
          ) {
            return url;
          }

          try {
            const urlObj = new URL(url);

            urlObj.searchParams.set(
              "t",
              Date.now()
            );

            return urlObj.toString();

          } catch (err) {
            return (
              url +
              (url.includes("?")
                ? "&"
                : "?") +
              `t=${Date.now()}`
            );
          }
        };

        if (parsedTemplate.previewImage) {
          const backgroundUrl =
            getCorsUrl(
              parsedTemplate.previewImage
            );

          const bg =
            await FabricImage.fromURL(
              backgroundUrl,
              {
                crossOrigin:
                  "anonymous",
              }
            );

          bg.set({
            left:
              canvas.width / 2,
            top:
              canvas.height / 2,
            originX: "center",
            originY: "center",
            selectable: false,
            evented: false,
          });

          bg.scaleToWidth(
            canvas.width
          );

          bg.scaleToHeight(
            canvas.height
          );

          canvas.backgroundImage = bg;
        }

        for (
          const item of
          parsedTemplate.layout || []
        ) {
          const left =
            (item.x / 100) *
            canvas.width;

          const top =
            (item.y / 100) *
            canvas.height;


          if (
            item.type === "text"
          ) {
            let extraStyles = {};

            try {
              let srcStr =
                item.Src ??
                item.src;

              if (srcStr) {
                const jsonStart =
                  srcStr.indexOf(
                    "{"
                  );

                if (
                  jsonStart !== -1
                ) {
                  srcStr =
                    srcStr.substring(
                      jsonStart
                    );

                  extraStyles =
                    JSON.parse(
                      srcStr
                    );
                }
              }

            } catch (err) {
              extraStyles = {};
            }

            canvas.add(
              new Textbox(
                item.text || "",
                {
                  left,
                  top,

                  width:
                    item.width ||
                    item.Width ||
                    canvas.width *
                    0.6,

                  fontSize:
                    item.fontSize ||
                    16,

                  fontFamily:
                    item.fontFamily ||
                    "Arial",

                  fontWeight:
                    item.fontWeight ||
                    extraStyles.fontWeight ||
                    "normal",

                  fontStyle:
                    item.fontStyle ||
                    extraStyles.fontStyle ||
                    "normal",

                  underline:
                    item.underline !==
                      undefined
                      ? item.underline
                      : extraStyles.underline ||
                      false,

                  fill:
                    item.color ||
                    "#000",

                  angle:
                    item.angle || 0,

                  textAlign:
                    item.textAlign ||
                    "left",

                  originX:
                    item.OriginX ||
                    item.originX ||
                    "center",

                  originY:
                    item.OriginY ||
                    item.originY ||
                    "center",

                  ...(
                    item.styles ||
                      extraStyles.styles
                      ? {
                        styles:
                          item.styles ||
                          extraStyles.styles,
                      }
                      : {}
                  ),

                  selectable: false,
                  evented: false,
                }
              )
            );
          }

          if (
            item.type === "image" ||
            item.type === "signature" ||
            item.type === "qr"
          ) {
            if (!item.src) {
              continue;
            }

            const imageUrl =
              getCorsUrl(
                item.src
              );

            const img =
              await FabricImage.fromURL(
                imageUrl,
                {
                  crossOrigin:
                    "anonymous",
                }
              );

            img.set({
              left,
              top,

              originX:
                item.originX ||
                "left",

              originY:
                item.originY ||
                "top",

              angle:
                item.angle || 0,

              selectable: false,
              evented: false,
            });

            const targetWidth =
              item.width
                ? item.width
                : (item.scaleX ||
                  0.5) *
                0.32 *
                canvas.width;

            if (item.width) {
              img.scaleToWidth(
                targetWidth
              );

              if (item.height) {
                img.scaleY =
                  (item.height /
                    img.getScaledHeight()) *
                  img.scaleY;
              }
            } else {
              img.scaleToWidth(
                targetWidth
              );
            }

            canvas.add(img);
          }

          if (
            item.type === "shape" &&
            item.shapeType === "line"
          ) {
            const x1 =
              item.X1 ??
              item.x1 ??
              0;

            const y1 =
              item.Y1 ??
              item.y1 ??
              0;

            const x2 =
              item.X2 ??
              item.x2 ??
              100;

            const y2 =
              item.Y2 ??
              item.y2 ??
              100;

            const shape =
              new Line(
                [x1, y1, x2, y2],
                {
                  left,
                  top,

                  originX:
                    item.OriginX ??
                    item.originX ??
                    "left",

                  originY:
                    item.OriginY ??
                    item.originY ??
                    "top",

                  stroke:
                    item.stroke ||
                    "#000",

                  strokeWidth:
                    item.strokeWidth ||
                    2,

                  strokeDashArray:
                    item.dash || [],

                  selectable: false,
                  evented: false,
                }
              );

            shape.set({
              scaleX: item.scaleX || 1,
              scaleY: item.scaleY || 1,
              angle: item.angle || 0,
            });

            canvas.add(shape);
          }
        }

        await document.fonts.ready;
        canvas.renderAll();

        const dataURL =
          canvas.toDataURL({
            format: "png",
            quality: 1,
            multiplier: 2,
          });

        const response = await fetch(dataURL);

        const blob = await response.blob();

        return new File(
          [blob],
          `${rowData.certificateNo ||
          "Certificate"
          }.jpg`,
          {
            type: "image/jpg",
          }
        );

      } finally {
        canvas.dispose();
      }
    };

  const generateCertificateAPI = async (certificate, certificateImage) => {
    const formData = new FormData();
    formData.append("certificateImage", certificateImage);
    return await saveTraineeCertificateImage(certificate.id, formData);
  };


  const handleBackToIssuance = () => {
    
    if (!generationCompleted && !generationFailed) {
      showAlert({
        title: "Certificate Generation in Progress",
        message:
          "Certificate generation is still in progress. If you leave this page, the remaining certificates may not be generated. Are you sure you want to leave?",
        type: "warning",
        showCancel: true,
        confirmStyle: "deep-blue",
        confirmText: "Leave",
        cancelText: "Stay",
        onConfirm: (closeAlert) => {
          closeAlert();
          dispatch({ type: traineesCertificate, payload: {}, });
           navigate(
          role === "SUPER_ADMIN"
       ? `/super-admin/certificate/issuance/${encodedId}/certificates`
      : `/admin/certificate/issuance/${encodedId}/certificates`
        );
        },
      });
      return;
    }

    dispatch({ type: traineesCertificate, payload: {}, });
    navigate(role === "SUPER_ADMIN"
   ? `/super-admin/certificate/issuance/${encodedId}/certificates`
      : `/admin/certificate/issuance/${encodedId}/certificates`
    );
  };


  const traineeName = currentCertificate?.traineeName || "";

  return (
    <>
      <AdminHeader />

      <Container
        fluid
        className="mt--6"
      >
        <Row>
          <Col md="12">
            <Card className="certificate-generation-card mt-3">
              <CardBody>
                <div className="certificate-generation-heading">
                  <h3>Generating Certificates for Batch: {batchDetails?.title || "N/A"}</h3>

                  <h5>Please wait while the system prepares the certificates for the selected trainees.</h5>

                  {!generationCompleted &&
                    !generationFailed && (
                      <div className="certificate-auto-message">
                        The certificates are being created one by one. Please wait until the process is complete.
                      </div>
                    )}
                </div>
                <hr className="mt-1 mb-2" />

                <div className="certificate-progress-wrapper">
                  <div className="certificate-progress-header">
                    <span>
                      Certificate Generation Progress
                    </span>

                    <strong>
                      {completedCount} /{" "}
                      {totalCertificates}
                    </strong>
                  </div>

                  <Progress
                    value={
                      progressPercentage
                    }
                    className="certificate-main-progress"
                  />

                  <div className="certificate-progress-footer">
                    <span>
                      {completedCount}{" "}
                      certificates completed
                    </span>

                    <span>
                      {pendingCount} remaining
                    </span>
                  </div>
                </div>

                {!generationCompleted &&
                  !generationFailed &&
                  currentCertificate && (

                    <div className="certificate-generating-area">

                      <div className="certificate-g-preview-wrapper">

                        <div className="certificate-g-preview">

                          {currentCertificate.parsedTemplate ? (
                            <ThumbnailPreview
                              parsedTemplate={
                                currentCertificate.parsedTemplate
                              }
                            />
                          ) : (
                            <div className="text-center py-5">
                              <i className="fas fa-certificate fa-3x text-muted" />
                            </div>
                          )}

                          <div className="certificate-preview-overlay">
                            <i className="fa fa-file-pdf" />
                            <span>
                              PDF
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="certificate-generating-text">
                        <div className="certificate-generating-status">
                          <i className="fa fa-spinner fa-spin" />
                          <span>
                            Generating certificate
                          </span>
                        </div>

                        <h4>
                          for {traineeName}
                        </h4>

                        <p>
                          Certificate{" "}
                          {completedCount + 1}{" "}
                          of{" "}
                          {totalCertificates}
                        </p>
                      </div>
                    </div>
                  )}

                {generationFailed && (
                  <div className="certificate-generation-completed">
                    <div className="completed-icon">
                      <i className="fa fa-exclamation-triangle" />
                    </div>

                    <h3>
                      Certificate Generation Failed
                    </h3>

                    <p>
                      We were unable to generate the
                      certificate
                      {traineeName
                        ? ` for ${traineeName}.`
                        : "."}
                    </p>

                    {error && (
                      <div className="generation-error">
                        <i className="fa fa-exclamation-circle mr-2" />
                        <span>
                          {error}
                        </span>
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn btn-primary mt-3"
                      onClick={
                        handleBackToIssuance
                      }
                    >
                      <i className="fa fa-arrow-left mr-2" />
                      Back to Certificate Issuance
                    </button>
                  </div>
                )}

                {generationCompleted && (
                  <div className="certificate-generation-completed">
                    <div className="completed-icon">
                      <i className="fa fa-check" />
                    </div>
                    <h3>
                      Certificate Generation Completed
                    </h3>
                    <p>
                      All{" "}
                      <strong>
                        {totalCertificates}
                      </strong>{" "}
                      certificates have been
                      successfully generated.
                    </p>

                    <div className="completed-status">
                      <i className="fa fa-check-circle" />
                      <span>
                        All certificates are ready
                      </span>
                    </div>

                    <button
                      type="button"
                      className="btn btn-primary mt-4"
                      onClick={
                        handleBackToIssuance
                      }
                    >
                      <i className="fa fa-arrow-left mr-2" />
                      Back to Certificate Issuance
                    </button>
                  </div>
                )}

              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default CertificateGenerationProgress;